import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/date'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'

const ActionLabels = {
  // Auth
  LOGIN_SUCCESS: '로그인 성공',
  LOGIN_FAILURE: '로그인 실패',
  LOGIN_LOCKED: '로그인 잠금',
  LOGIN_BLOCKED_STATUS: '로그인 차단(계정상태)',
  PASSWORD_RESET_TEMP_PASSWORD_ISSUED: '비밀번호 찾기 임시 비밀번호 발급',
  PASSWORD_RESET_TEMP_PASSWORD_DELIVERY_FAILED: '비밀번호 찾기 임시 비밀번호 전달 실패',
  PASSWORD_RESET_SUCCESS: '비밀번호 재설정 완료',

  // Users
  USER_CREATE: '사용자 생성',
  USER_UPDATE: '사용자 정보 변경',
  USER_ACTIVATE: '사용자 활성화',
  USER_DEACTIVATE: '사용자 비활성화',
  USER_PASSWORD_RESET: '비밀번호 초기화(운영자)',

  ADD_GA_BRANCH_MAPPING: '원수사 담당자↔GA 매핑 추가',
  REMOVE_GA_BRANCH_MAPPING: '원수사 담당자↔GA 매핑 제거',

  // Requests
  REQUEST_CREATED: '의뢰 생성',
  REQUEST_ASSIGNED: '의뢰 배정',
  REQUEST_REASSIGN: '의뢰 재배정',
  REQUEST_COMPLETE: '의뢰 타겟 완료',
  REQUEST_COMPLETED: '의뢰 완료',
  REQUEST_CANCEL: '의뢰 취소',
  REQUEST_REREQUEST: '의뢰 재요청',
  REQUEST_APPROVE_COMPLETION: '의뢰 완료 승인',

  REQUEST_TARGET_START_PROGRESS: '타겟 진행 시작',
  REQUEST_TARGET_QUESTION: '문의 등록',
  REQUEST_TARGET_QUESTION_RESOLVED: '문의 해결',
  REQUEST_TARGET_CANCEL: '타겟 취소',

  // Notices
  NOTICE_APPROVE: '공지 승인',
  NOTICE_SEND: '공지 발송',

  // Health
  HEALTH_REPORT_CREATE: '진료분석 리포트 생성',
  HEALTH_REPORT_VIEW: '진료분석 리포트 조회',
  HEALTH_REPORT_REANALYZE: '진료분석 재분석',
  HEALTH_REPORT_EXPORT: '진료분석 결과 다운로드',

  // (legacy / accidental)
  변경이력: '변경이력 기록',
}

function labelForAction(action) {
  if (!action) return ''
  return ActionLabels[action] || '기타'
}

function labelForResource(resource) {
  if (!resource) return ''
  const r = String(resource)
  const m1 = r.match(/^users:(\d+)$/)
  if (m1) return `사용자 #${m1[1]}`
  const m2 = r.match(/^user:(\d+)/)
  if (m2) return `사용자 #${m2[1]}`
  const m3 = r.match(/^org:(\d+)$/)
  if (m3) return `조직 #${m3[1]}`
  const m4 = r.match(/^request:(\d+)$/)
  if (m4) return `의뢰 #${m4[1]}`
  const m5 = r.match(/^target:(\d+)$/)
  if (m5) return `의뢰 타겟 #${m5[1]}`
  const m6 = r.match(/^notice:(\d+)$/)
  if (m6) return `공지 #${m6[1]}`
  const m7 = r.match(/^healthReport:(\d+)$/)
  if (m7) return `진료분석 리포트 #${m7[1]}`
  return r
}

function toIsoOrEmpty(v) {
  if (!v) return ''
  try {
    const d = new Date(v)
    if (Number.isNaN(d.getTime())) return ''
    return d.toISOString()
  } catch {
    return ''
  }
}

export default function AuditLogsPage() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [q, setQ] = useState('')
  const [action, setAction] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(0)
  const [size] = useState(50)
  const [result, setResult] = useState({ content: [], totalElements: 0, totalPages: 0, number: 0, size: 50 })

  const actionOptions = useMemo(() => {
    const keys = Object.keys(ActionLabels || {})
    keys.sort()
    return keys
  }, [])

  const params = useMemo(() => {
    const p = new URLSearchParams()
    if (q) p.set('q', q)
    if (action) p.set('action', action)
    if (from) p.set('from', toIsoOrEmpty(from))
    if (to) p.set('to', toIsoOrEmpty(to))
    p.set('page', String(page))
    p.set('size', String(size))
    return p.toString()
  }, [q, action, from, to, page, size])

  useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const res = await api.get(`/audit-logs?${params}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) setResult(payload || { content: [] })
      } catch {
        if (!ignore) setError('변경이력을 불러오지 못했습니다')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [params])

  if (loading && (!result?.content || result.content.length === 0)) return <Loading />
  if (error) return <InlineError message={error} />

  const content = Array.isArray(result?.content) ? result.content : []

  return (
    <div className="p-4">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold">변경이력</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input className="rounded border px-3 py-2 text-sm" placeholder="검색(행위자/행동/대상)" value={q} onChange={(e) => { setPage(0); setQ(e.target.value) }} />
          <select className="rounded border px-3 py-2 text-sm" value={action} onChange={(e) => { setPage(0); setAction(e.target.value) }}>
            <option value="">행동 전체</option>
            {actionOptions.map(code => (
              <option key={code} value={code}>{labelForAction(code)} ({code})</option>
            ))}
          </select>
          <input className="rounded border px-3 py-2 text-sm" type="date" value={from} onChange={(e) => { setPage(0); setFrom(e.target.value) }} />
          <input className="rounded border px-3 py-2 text-sm" type="date" value={to} onChange={(e) => { setPage(0); setTo(e.target.value) }} />
        </div>
      </div>

      <div className="overflow-x-auto rounded border bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2">시간</th>
              <th className="px-3 py-2">조직</th>
              <th className="px-3 py-2">사용자</th>
              <th className="px-3 py-2">행동</th>
              <th className="px-3 py-2">대상</th>
            </tr>
          </thead>
          <tbody>
            {content.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-center text-gray-500" colSpan={5}>데이터가 없습니다</td>
              </tr>
            )}
            {content.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="font-medium">{row.orgName || '-'}</div>
                  {row.orgId != null && <div className="text-xs text-gray-500">#{row.orgId}</div>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="font-medium">{row.userName || row.actor || '-'}</div>
                  {row.userName && row.actor && (<div className="text-xs text-gray-500">{row.actor}</div>)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="font-medium">{labelForAction(row.action)}</div>
                  <div className="text-xs text-gray-500">{row.action}</div>
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{labelForResource(row.resource)}</div>
                  <div className="text-xs text-gray-500">{row.resource}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-sm">
        <div className="text-gray-600">
          총 {result?.totalElements ?? content.length}건
        </div>
        <div className="flex gap-2">
          <button className="rounded border px-3 py-1 disabled:opacity-50" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>이전</button>
          <button className="rounded border px-3 py-1 disabled:opacity-50" disabled={result?.totalPages != null ? page >= (result.totalPages - 1) : false} onClick={() => setPage(p => p + 1)}>다음</button>
        </div>
      </div>
    </div>
  )
}

